import { useEffect, useState } from 'react'
import api from '../services/api'

/**
 * Admin-visible runtime feature flags, read from the public GET /services/settings
 * (the same endpoint the customer app reads). Today it carries one flag the admin
 * cares about: whether WhatsApp Blast is switched on. The flag is owned by the
 * backend and flipped from Railway (ALLOW_WHATSAPP_BLAST), so the dashboard never
 * needs a redeploy to hide or show the feature.
 */
export interface FeatureFlags {
  whatsappBlast: boolean
  /** Orders section (sidebar entry + /orders route). Mirrors ALLOW_ADMIN_ORDERS. */
  adminOrders: boolean
}

// Fail CLOSED while we do not yet know: the feature is gated off by default, so
// hiding it until the server confirms it is on avoids flashing a nav link that
// would immediately 403.
// whatsappBlast fails CLOSED (it is off by default, so hiding it until the
// server confirms avoids flashing a link that would immediately 403).
// adminOrders fails OPEN: Orders is the core of the portal, and a settings
// outage must never be the reason an operator loses access to it. Only an
// explicit `false` from the server takes it away.
const DEFAULT_FLAGS: FeatureFlags = { whatsappBlast: false, adminOrders: true }

// One request per page load, shared by the sidebar and the route guard.
let cached: Promise<FeatureFlags> | null = null

const fetchFlags = (): Promise<FeatureFlags> => {
  if (!cached) {
    cached = api
      .get('/services/settings')
      .then((res) => {
        const data = res.data?.data ?? {}
        return {
          whatsappBlast: String(data.whatsapp_blast_enabled ?? 'false') === 'true',
          // Absent key (older backend) must mean ON, matching the fail-open default.
          adminOrders: String(data.admin_orders_enabled ?? 'true') === 'true',
        }
      })
      .catch(() => {
        // A settings outage must not strand the operator. Fail closed, but let a
        // later call retry rather than caching the failure forever.
        cached = null
        return DEFAULT_FLAGS
      })
  }
  return cached
}

export const useFeatureFlags = (): { flags: FeatureFlags; loaded: boolean } => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let active = true
    void fetchFlags().then((resolved) => {
      if (active) {
        setFlags(resolved)
        setLoaded(true)
      }
    })
    return () => {
      active = false
    }
  }, [])

  return { flags, loaded }
}
